import { useState } from 'react';
import type { BankDifference } from '../types';
import { amountAria, money, signedMoney } from '../utils';
import { Check, Spinner } from './common';
import { answerDifference, postDifference } from '../store';
import { CoverageLine } from './Coverage';
import { isPlainClick } from '@/components/plainClick';

const DIFF_META: Record<BankDifference['kind'], string> = {
  takeback: 'Less than posted',
  interest: 'More than posted',
  money_no_post: 'Money in, nothing posted',
  post_no_money: 'Posted, no money',
  two_fit: 'Two payments could fit',
  unmatched: 'Unmatched by you',
};

function DiffRow({ d, selected, onSelect }: { d: BankDifference; selected: boolean; onSelect: (id: string) => void }) {
  const signed = d.kind === 'takeback' || d.kind === 'interest';
  return (
    <a href={`/posting/bank/${d.id}`} className={`po-qrow${selected ? ' po-qsel' : ''}`} aria-current={selected ? 'page' : undefined} onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); onSelect(d.id); }}>
      <div className="po-qtop">
        <span className="po-qname">{d.payer}</span>
        <span className="po-qamt num" aria-label={amountAria(d.diff)}>{signed ? signedMoney(d.diff, { plus: d.kind === 'interest' }) : money(d.diff)}</span>
      </div>
      <div className="po-qsub">{d.dateLine}</div>
      <div className="po-qtagline"><span className="po-badge b-warn">Doesn’t match</span><span>{DIFF_META[d.kind]}</span></div>
    </a>
  );
}

export function BankList({ differences, selectedId, onSelect }: { differences: BankDifference[]; selectedId?: string; onSelect: (id: string) => void }) {
  return (
    <div className="po-queue">
      <CoverageLine className="po-coverage" />
      <p className="po-group-head">Don’t match · <span className="po-count">{differences.length}</span></p>
      {differences.length === 0 ? <p className="po-meta" style={{ padding: '14px 16px' }}>Every deposit on the statements matches. Nothing is waiting for you here.</p> : null}
      {differences.map((d) => <DiffRow key={d.id} d={d} selected={d.id === selectedId} onSelect={onSelect} />)}
    </div>
  );
}

function HandledCard({ d, onOpenClaims }: { d: BankDifference; onOpenClaims?: () => void }) {
  if (!d.handled) return null;
  return (
    <section className="po-handled">
      <div className="po-handled-badge"><Check /> ELVA handled it</div>
      {d.handled.ticks.map((t, i) => (
        <div key={`ht-${String(i)}`} className="po-tickrow">
          <span className="po-tick" aria-hidden="true"><Check /></span>
          <div>
            {t.text}
            {t.links ? <span>{t.links.map((l) => <span key={l.label}> · <button type="button" className="po-src">{l.label}</button></span>)}</span> : null}
          </div>
        </div>
      ))}
      {d.handled.openClaims ? (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="po-btn po-btn-ghost po-btn-sm" onClick={onOpenClaims}>{d.handled.openClaims.summary}</button>
        </div>
      ) : null}
      {d.choices.length === 0 ? <p className="po-meta" style={{ marginTop: 10 }}>Nothing for you to decide here.</p> : null}
    </section>
  );
}

export function BankDetail({ d, onBack, onLeave, onOpenToPost }: { d: BankDifference; onBack?: () => void; onLeave: () => void; onOpenToPost?: () => void }) {
  const [posting, setPosting] = useState(false);
  const [step, setStep] = useState('');
  const [done, setDone] = useState(false);
  const signed = d.kind === 'takeback' || d.kind === 'interest';

  const runPost = async (): Promise<void> => {
    setPosting(true);
    for await (const ev of postDifference(d.id)) {
      if ('step' in ev) setStep(ev.step);
    }
    setPosting(false);
    setDone(true);
  };

  const onChoice = (choice: string): void => {
    const opt = d.choices.find((c) => c.id === choice);
    if (opt?.effect === 'post_fix') { void runPost(); return; }
    answerDifference(d.id, choice);
    onLeave();
  };

  return (
    <main className="po-detail">
      {onBack ? <button type="button" className="po-back" onClick={onBack}>← All deposits</button> : null}
      <div className="po-detail-inner">
        <section className="po-claimhead">
          <div>
            <h2 className="po-patient" tabIndex={-1} style={{ fontSize: 22 }}>{d.headline}</h2>
            <p className="po-chsub">{d.payer} · {DIFF_META[d.kind]}</p>
            <p className="po-chmeta">{d.dateLine}</p>
          </div>
          <div className="po-right"><div className="po-meta">Difference</div><div className="po-bigfig num" aria-label={amountAria(d.diff)}>{signed ? signedMoney(d.diff, { plus: d.kind === 'interest' }) : money(d.diff)}</div></div>
        </section>

        <section className="po-card">
          <p style={{ margin: 0, color: 'var(--fg2)' }}>{d.body}</p>
          {d.loggedLine ? <p className="po-meta" style={{ margin: '8px 0 0' }}>{d.loggedLine}</p> : null}
        </section>

        {d.breakdown ? (
          <section className="po-card">
            <h3 className="po-h3" style={{ marginBottom: 6 }}>What the ERA and the bank show</h3>
            <table className="po-break"><tbody>{d.breakdown.map((r, i) => (
              <tr key={`b-${String(i)}`} className={r.kind === 'total' ? 'total' : undefined}><td>{r.label}</td><td className={`amt${r.kind === 'neg' ? ' neg' : ''}`} aria-label={amountAria(r.amount)}>{r.kind === 'neg' ? signedMoney(r.amount) : money(r.amount)}</td></tr>
            ))}</tbody></table>
          </section>
        ) : null}

        {d.check ? (
          <section className="po-card">
            <h3 className="po-h3" style={{ marginBottom: 8 }}>Was it an overpayment?</h3>
            <div className="po-timing" style={d.check.ok ? undefined : { background: 'var(--wsoft)' }}>
              <span className="po-tick" aria-hidden="true" style={d.check.ok ? undefined : { background: 'var(--card)', color: 'var(--warning)' }}>{d.check.ok ? <Check /> : '!'}</span>
              <div>{d.check.text}</div>
            </div>
          </section>
        ) : null}

        <HandledCard d={d} onOpenClaims={onOpenToPost ?? onLeave} />

        {d.metaLine ? <p className="po-meta">{d.metaLine}</p> : null}

        {done || d.posted ? (
          <div className="po-answer"><span className="po-badge b-ok"><Check /></span><span>Posted and checked. Moved to Matched.</span></div>
        ) : posting ? (
          <div className="po-live" aria-live="polite"><Spinner /><span>{step || 'Posting…'}</span></div>
        ) : d.choices.length > 0 ? (
          <section className="po-card">
            {d.prompt ? <h3 className="po-h3" style={{ marginBottom: 10 }}>{d.prompt}</h3> : null}
            <div className="po-actionbar" style={{ marginTop: 0 }}>
              {d.choices.map((c) => (
                <button key={c.id} type="button" className={`po-btn ${(c.primary ?? false) ? 'po-btn-primary po-btn-lg' : ''}`} onClick={() => { onChoice(c.id); }}>{c.label}</button>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
}
